import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Block,
  BlockTitle,
  Button,
  Card,
  Notification,
  Link,
  List,
  ListInput,
  ListItem,
  Navbar,
  Page,
  Preloader,
  Progressbar,
  Sheet,
  Toolbar,
  Checkbox,
} from 'konsta/react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserProduct } from '../models/Models';
import { Get, Post } from '../helpers/AxiosHelper';
import { formatMinerUSD, formatUSD } from '../utils';
import moment from 'moment';
import TabBar from '../components/TabBar';
import { useAppContext } from '../context/AppContext';
import { UserProductActivityCd, UserProductOwnershipTypeCd } from '../enums';
import { FaArrowLeftLong, FaCheck, FaStore } from 'react-icons/fa6';
import { FaLongArrowAltRight } from 'react-icons/fa';
import { MdError } from 'react-icons/md';
import { useRefetchQuery } from '../context/useRefetchQuery';
import { hapticFeedback, popup } from '@telegram-apps/sdk';
import Lottie from 'lottie-react';
import { BiTransfer } from 'react-icons/bi';

import lottieLoading from '../lotties/loading 10.json';
import { mixTrack } from '../helpers/MixPanelHelper';

const MIN_MINER_SELL_PRICE = 50;
const MAX_MINER_SELL_PRICE = 50_000;

const MyMinerDetailsPage = () => {
  const refetchQuery = useRefetchQuery();
  const { userProducts, balance } = useAppContext();
  const { productId } = useParams();
  const [product, setProduct] = useState<UserProduct>();
  const [notFound, setNotFound] = useState<boolean>();
  const [isListingOnMarketplace, setIsListingOnMarketplace] =
    useState<boolean>(false);
  const [isTransferring, setIsTransferring] = useState<boolean>(false);
  const [sheetOpened, setSheetOpened] = useState(false);
  const [sheetTransferOpened, setSheetTransferOpened] = useState(false);
  const [sellStage, setSellStage] = useState<'intro' | 'settings' | 'done'>(
    'intro',
  );

  const [transferStage, setTransferStage] = useState<
    'intro' | 'settings' | 'done'
  >('intro');

  const [newOwnerCustomerId, setNewOwnerCustomerId] = useState<string>();
  const [agreeToTransferTerms, setAgreeToTransferTerms] =
    useState<boolean>(false);

  const [walletAddress, setWalletAddress] = useState<string>();
  const [sellPrice, setSellPrice] = useState<string>(
    MIN_MINER_SELL_PRICE.toString(),
  );
  const [notificationFull, setNotificationFull] = useState(false);
  const [error, setError] = useState();
  const navigate = useNavigate();

  useEffect(() => {
    if (!productId || isNaN(Number(productId))) {
      setNotFound(true);
      return;
    }
    if (userProducts) {
      const _product = userProducts.filter(
        (c) => c.id === Number(productId),
      )[0];
      if (_product) {
        setProduct(_product);
      } else {
        setNotFound(true);
        return;
      }
    }
  }, [userProducts, balance]);

  async function listOnMarketplace() {
    if (hapticFeedback.impactOccurred.isAvailable()) {
      hapticFeedback.impactOccurred('medium');
    }

    const _sellPrice = Number(sellPrice);

    if (
      !product ||
      !sellPrice ||
      _sellPrice < MIN_MINER_SELL_PRICE ||
      _sellPrice > MAX_MINER_SELL_PRICE ||
      !walletAddress
    ) {
      return;
    }

    setIsListingOnMarketplace(true);

    const result = await Post<
      any,
      { userProductId: number; price: number; walletAddress: string }
    >('/api/markForSale', {
      userProductId: product.id,
      price: _sellPrice,
      walletAddress: walletAddress.trim(),
    });

    if (typeof result === 'object' && result.error) {
      setError(result.error);
      setNotificationFull(true);
      setTimeout(() => {
        setNotificationFull(false);
      }, 5000);
      setIsListingOnMarketplace(false);

      if (hapticFeedback.notificationOccurred.isAvailable()) {
        hapticFeedback.notificationOccurred('error');
      }
      return;
    }

    setSellStage('done');
    setIsListingOnMarketplace(false);

    if (hapticFeedback.notificationOccurred.isAvailable()) {
      hapticFeedback.notificationOccurred('success');
    }

    refetchQuery(['user_products']);
    refetchQuery(['products']);

    mixTrack('Marketplace Listing Confirmed', {
      productId: product.product?.id,
      name: product.name,
    });
  }

  async function submitTransfer() {
    if (hapticFeedback.impactOccurred.isAvailable()) {
      hapticFeedback.impactOccurred('medium');
    }

    const _newOwnerCustomerId = Number(newOwnerCustomerId);

    if (!product || isNaN(_newOwnerCustomerId)) {
      return;
    }

    let canContinue = true;

    if (popup.isSupported()) {
      const promise = await popup.open({
        title: 'Withdraw',
        message: 'Are you sure you want to proceed with the transfer?',
        buttons: [
          {
            text: 'Yes',
            type: 'default',
            id: 'yes',
          },
          {
            type: 'cancel',
            id: 'cancel',
          },
        ],
      });

      await promise;

      const buttonId = await promise;

      canContinue = buttonId === 'yes';
    }

    if (!canContinue) {
      return;
    }

    setIsTransferring(true);

    const result = await Post<
      any,
      { userProductId: number; customerId: number }
    >('/api/transferMiner', {
      userProductId: product.id,
      customerId: _newOwnerCustomerId,
    });

    if (typeof result === 'object' && result.error) {
      setError(result.error);
      setNotificationFull(true);
      setTimeout(() => {
        setNotificationFull(false);
      }, 5000);
      setIsTransferring(false);

      if (hapticFeedback.notificationOccurred.isAvailable()) {
        hapticFeedback.notificationOccurred('error');
      }
      return;
    }

    if (hapticFeedback.notificationOccurred.isAvailable()) {
      hapticFeedback.notificationOccurred('success');
    }

    refetchQuery(['user_products']);

    mixTrack('Transfer Completed', {
      productId: product.product?.id,
      productName: product.product?.name,
      name: product.name,
      newCustomerId: _newOwnerCustomerId,
    });

    navigate('/my-miners');
  }

  async function cancelMarketplaceListing() {
    if (!product) {
      return;
    }

    setIsListingOnMarketplace(true);

    const result = await Post<any, { userProductId: number }>(
      '/api/cancelProductSale',
      {
        userProductId: product.id,
      },
    );

    if (typeof result === 'object' && result.error) {
      setError(result.error);
      setNotificationFull(true);
      setTimeout(() => {
        setNotificationFull(false);
      }, 5000);
      setIsListingOnMarketplace(false);
      return;
    }

    setIsListingOnMarketplace(false);

    refetchQuery(['user_products']);
    refetchQuery(['products']);
  }

  return (
    <Page className=" pb-40 select-none">
      <Navbar
        title="Miner Details"
        subtitle={product && product.name}
        className="top-0 sticky translucent "
        left={
          <FaArrowLeftLong
            className="mx-3 w-5 h-5"
            onClick={() => navigate(-1)}
          />
        }
      />

      <Notification
        opened={notificationFull}
        icon={<MdError className="text-red-500" />}
        title="Error"
        subtitle="Could not process your request"
        text={error}
        translucent
        className="absolute top-10"
      />

      {product && (
        <>
          {product &&
            product.activityCd === UserProductActivityCd.SettingUp && (
              <Block className="space-y-1">
                <span className="text-primary">Setup in progress</span>
                <Progressbar
                  className="k-color-brand-green mt-3"
                  progress={
                    product.setupProgress > 0 ? product.setupProgress / 100 : 0
                  }
                />
                <span className="text-primary float-end">
                  {product.setupProgress.toFixed(2)}%
                </span>
              </Block>
            )}

          {product.activityCd === UserProductActivityCd.Active && (
            <div className="flex justify-center items-end text-center currency text-primary tracking-widest text-4xl mt-4">
              {formatMinerUSD(product.currentBalance)}
              <div>
                <Lottie animationData={lottieLoading} className="w-8 h-8" />
              </div>
            </div>
          )}

          <BlockTitle>Miner Details</BlockTitle>
          <List strong>
            <ListItem title="Name" after={<span>{product.name}</span>} />
            <ListItem
              title="Type"
              after={<span>{product.product.name}</span>}
            />
            <ListItem
              title="Status"
              after={
                <span>
                  {product.activityCd === UserProductActivityCd.Active ? (
                    <div className="text-primary flex items-center">
                      Online{' '}
                      <span className="relative flex size-3 ms-3">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75"></span>
                        <span className="relative inline-flex size-3 rounded-full bg-primary"></span>
                      </span>
                    </div>
                  ) : product.activityCd === UserProductActivityCd.SettingUp ? (
                    <span className="text-blue-500">Setting Up...</span>
                  ) : product.activityCd === UserProductActivityCd.Stopped ? (
                    'Stopped'
                  ) : (
                    '-'
                  )}
                </span>
              }
            />
            <ListItem
              title="Price Bought"
              after={<span>{formatUSD(product.priceBought)}</span>}
            />
            <ListItem
              title="Bought"
              after={
                <span className="">
                  {moment.utc(product.boughtAt).local().calendar()}
                </span>
              }
            />
            <ListItem
              title="Ownership Type"
              after={
                <span>
                  {product.ownershipType ===
                  UserProductOwnershipTypeCd.Dedicated
                    ? 'Dedicated'
                    : 'Shared'}
                </span>
              }
            />
            <ListItem
              title="Avg. Profit/Day"
              after={<span>{formatUSD(product.product.avgProfitDay)}</span>}
            />

            {product.activityCd === UserProductActivityCd.Active && (
              <ListItem
                title="Current Balance"
                after={
                  <span className="currency text-primary tracking-widest">
                    {formatMinerUSD(product.currentBalance)}
                  </span>
                }
              />
            )}

            {product.activityCd === UserProductActivityCd.Active && (
              <ListItem
                title="Total Income"
                after={
                  <span className="currency tracking-widest">
                    {formatMinerUSD(product.totalIncome)}
                  </span>
                }
              />
            )}
          </List>

          <Block>
            {/* <Button disabled={product.activityCd === UserProductActivityCd.SettingUp} small colors={{fillBgMaterial: 'bg-yellow-500', textMaterial: 'text-black'}}>
                Transfer ownership <BiTransfer className="ms-3"/>
            </Button> */}

            {product.forSale && (
              <span className="text-yellow-500">
                This product has been listed to be sold on the marketplace
              </span>
            )}

            {!product.forSale && (
              <Button
                disabled={
                  product.activityCd === UserProductActivityCd.SettingUp
                }
                small
                colors={{
                  fillBgMaterial: 'bg-yellow-500',
                  textMaterial: 'text-black',
                }}
                onClick={() => {
                  setSellStage('intro');
                  setSheetOpened(true);
                  mixTrack('[Click] Sell on Marketplace', {
                    productId: product.product?.id,
                    name: product.name,
                  });
                }}
              >
                Sell on Marketplace <FaStore className="ms-3" />
              </Button>
            )}

            {product.forSale && (
              <Button
                disabled={
                  product.activityCd === UserProductActivityCd.SettingUp
                }
                small
                colors={{
                  fillBgMaterial: 'bg-red-500',
                  textMaterial: 'text-white',
                }}
                onClick={() => {
                  cancelMarketplaceListing();
                  mixTrack('[Click] Cancel Marketplace Listing', {
                    productId: product.product?.id,
                    name: product.name,
                  });
                }}
              >
                Cancel listing{' '}
                {!isListingOnMarketplace && <FaStore className="ms-3" />}{' '}
                {isListingOnMarketplace && (
                  <>
                    <Preloader
                      size="w-4 h-4"
                      colors={{ iconMaterial: 'text-primary' }}
                      className="ms-2"
                    />
                  </>
                )}
              </Button>
            )}

            {
              <Button
                disabled={
                  product.activityCd === UserProductActivityCd.SettingUp ||
                  product.forSale
                }
                small
                colors={{
                  fillBgMaterial: 'bg-white',
                  textMaterial: 'text-black',
                }}
                className="mt-3"
                onClick={() => {
                  setTransferStage('intro');
                  setSheetTransferOpened(true);
                }}
              >
                Transfer Ownership <BiTransfer className="ms-3" />
              </Button>
            }

            <br />
            <br />
            <br />
            <br />
          </Block>

          {/* Sell Sheet */}
          <Sheet
            className="pb-safe w-full"
            opened={sheetOpened}
            onBackdropClick={() => setSheetOpened(false)}
          >
            <Toolbar top>
              <div className="left">Sell on Marketplace</div>
              <div className="right">
                <Link toolbar onClick={() => setSheetOpened(false)}>
                  Close
                </Link>
              </div>
            </Toolbar>
            <Block>
              {sellStage === 'intro' && (
                <>
                  <p>
                    You can list your miners for sale on our marketplace and let
                    other users buy them directly. Enjoy a seamless and secure
                    transaction process. Your miner will continue mining and
                    generating earnings until it is sold. Once a buyer purchases
                    your miner, the final earnings accumulated before the sale,
                    along with the sale amount you set, will be automatically
                    transferred to the wallet address you provide in the next
                    step.
                  </p>
                  <br />
                  <p>
                    A <b>1%</b> service fee will be applied to each successful
                    sale.
                  </p>
                  <div className="mt-4">
                    <Button onClick={() => setSellStage('settings')}>
                      Next <FaLongArrowAltRight className="ms-2" />
                    </Button>
                  </div>
                </>
              )}

              {sellStage === 'settings' && (
                <>
                  <List strongIos insetIos>
                    <ListInput
                      outline
                      label="Price (USD)"
                      floatingLabel
                      type="number"
                      value={sellPrice}
                      onChange={(e) => {
                        const price = Number(e.currentTarget.value);
                        if (!isNaN(price)) {
                          setSellPrice(price.toString());
                        } else {
                        }
                      }}
                      onBlur={(e) => {
                        const price = Number(e.currentTarget.value);
                        if (isNaN(price)) {
                          setSellPrice(MIN_MINER_SELL_PRICE.toString());
                        } else if (!isNaN(price)) {
                          if (price < MIN_MINER_SELL_PRICE) {
                            setSellPrice(MIN_MINER_SELL_PRICE.toString());
                          } else if (price > MAX_MINER_SELL_PRICE) {
                            setSellPrice(MAX_MINER_SELL_PRICE.toString());
                          }
                        }
                      }}
                    />

                    <ListInput
                      outline
                      label="USDT (TRC-20) Wallet Address"
                      floatingLabel
                      value={walletAddress}
                      onChange={(e) =>
                        setWalletAddress(e.currentTarget.value.replace(' ', ''))
                      }
                      info="Once sold, the payout will be sent to this wallet"
                    />
                  </List>
                  <div className="mt-4">
                    <Button
                      onClick={() => listOnMarketplace()}
                      disabled={
                        !sellPrice ||
                        Number(sellPrice) < MIN_MINER_SELL_PRICE ||
                        Number(sellPrice) > MAX_MINER_SELL_PRICE ||
                        isListingOnMarketplace ||
                        !walletAddress ||
                        walletAddress.trim().length < 16
                      }
                    >
                      Submit{' '}
                      {isListingOnMarketplace && (
                        <>
                          <Preloader
                            size="w-4 h-4"
                            colors={{ iconMaterial: 'text-black' }}
                          />
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}

              {sellStage === 'done' && (
                <>
                  <div className="flex flex-col justify-center items-center">
                    <FaCheck className="w-36 h-36 text-primary mb-5" />
                    <h1 className="text-3xl font-bold mb-2">
                      Listed Successfully!
                    </h1>
                    <h3 className="text-center">
                      Miner has been listed on the marketplace successfully.
                    </h3>
                  </div>
                  <div className="mt-4">
                    <Button onClick={() => setSheetOpened(false)}>Done</Button>
                  </div>
                </>
              )}
            </Block>
          </Sheet>

          {/* Transfer Sheet */}
          <Sheet
            className="pb-safe w-full"
            opened={sheetTransferOpened}
            onBackdropClick={() => setSheetTransferOpened(false)}
          >
            <Toolbar top>
              <div className="left">Transfer Ownership</div>
              <div className="right">
                <Link toolbar onClick={() => setSheetTransferOpened(false)}>
                  Close
                </Link>
              </div>
            </Toolbar>
            <Block>
              {transferStage === 'intro' && (
                <>
                  <p>
                    Easily transfer your miner to another Telegram account. Once
                    transferred, the new owner will start earning from it
                    immediately.
                  </p>
                  <br />
                  <b className="font-bold">Important</b>
                  <ul className="list-disc pl-3">
                    <li>
                      Miner can only be transferred once every 24 hours. If
                      you've recently transferred a miner, you'll need to wait
                      before transferring it again.
                    </li>
                    <li>
                      Any unwithdrawn balance from the miner will also be
                      transferred to the new owner,{' '}
                      <b>
                        so be sure to withdraw your earnings before initiating
                        the transfer if needed.
                      </b>
                    </li>
                    <li>
                      Once the transfer is complete, you will no longer have
                      access to the miner or its earnings. The new owner will
                      have full control.
                    </li>
                    <li className="text-yellow-500">
                      MYNR is not responsible for transactions made outside of
                      the app. If you transfer a miner to someone in exchange
                      for payment outside of MYNR and get scammed, we cannot
                      assist in recovering your miner or funds. Always trade
                      safely!
                    </li>
                  </ul>

                  <List>
                    <ListItem
                      label
                      title="I understand that once I transfer my miner, I will lose access to it and its earnings. I also acknowledge that MYNR is not responsible for transactions outside the app, and I am solely responsible for any transfers I make."
                      media={
                        <Checkbox
                          component="div"
                          name="agree-transfer"
                          checked={agreeToTransferTerms}
                          onChange={() =>
                            setAgreeToTransferTerms(!agreeToTransferTerms)
                          }
                        />
                      }
                    />
                  </List>

                  <div className="mt-4">
                    <Button
                      disabled={!agreeToTransferTerms}
                      onClick={() => setTransferStage('settings')}
                    >
                      Next <FaLongArrowAltRight className="ms-2" />
                    </Button>
                  </div>
                </>
              )}

              {transferStage === 'settings' && (
                <>
                  <List>
                    <ListInput
                      outline
                      label="New Owner's Customer ID"
                      floatingLabel
                      type="number"
                      value={newOwnerCustomerId}
                      onChange={(e) =>
                        setNewOwnerCustomerId(
                          e.currentTarget.value.replace(' ', ''),
                        )
                      }
                      info="Enter the new owner's customer ID. Ensure the ID is correct."
                    />
                  </List>

                  <p className="my-2 text-yellow-500">
                    MYNR is not responsible for transactions made outside of the
                    app. If you transfer a miner to someone in exchange for
                    payment outside of MYNR and get scammed, we cannot assist in
                    recovering your miner or funds. Always trade safely!
                  </p>

                  {error && notificationFull && (
                    <div className="text-red-500 font-bold my-3">{error}</div>
                  )}

                  <div className="mt-4">
                    <Button
                      onClick={() => submitTransfer()}
                      disabled={
                        isTransferring ||
                        (newOwnerCustomerId?.length ?? 0) < 5 ||
                        (newOwnerCustomerId?.length ?? 0) > 15
                      }
                    >
                      Confirm & Transfer{' '}
                      {isTransferring && (
                        <>
                          <Preloader
                            size="w-4 h-4 ms-3"
                            colors={{ iconMaterial: 'text-primary' }}
                          />
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </Block>
          </Sheet>
        </>
      )}

      {notFound && (
        <Block className="text-center text-lg">Miner not found!</Block>
      )}

      <TabBar selectedTab="home" />
    </Page>
  );
};

export default MyMinerDetailsPage;
